// Available add-ons — Stream 3 PR-2. Custom-quote listings only.
//
// Reads publicData.addOnCategories (written by the ADDONS wizard
// step in PR-1). Groups by tier (Decor / F&B / Production) and
// renders each as a card with the host's startingAt price,
// description, and a verified badge when the category requires a
// credential that the host has on file.
//
// The verified badge is purely informational here — gating happens
// at the wizard's REVIEW step (Q3 lock). If the host published with
// unverified credentials (shouldn't happen, but the wizard could
// drift), this section still renders the card without the badge.

import React from 'react';
import classNames from 'classnames';

import {
  ADDON_TIERS,
  ADDON_TIER_LABELS,
  findAddonCategory,
} from '../../../data/addon-categories';
import { startingPriceUnitLabel } from '../../../util/editListingHelpers';
import { priceWithFees } from '../../../util/listingHelpers';
import { ensureUser } from '../../../util/data';

import css from './SectionAvailableAddOns.module.css';

const formatStartingAt = (cents, pricingUnit) => {
  if (typeof cents !== 'number' || !Number.isFinite(cents) || cents <= 0) {
    return null;
  }
  const dollars = cents / 100;
  const amount =
    dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
  // Shared vocabulary: "$50 per item", "$25 per person/guest", … The card
  // prefixes "from", so this is the floor amount + its unit. Legacy
  // per_guest rows resolve to "per person/guest" via the label helper.
  return `${amount} ${startingPriceUnitLabel(pricingUnit)}`;
};

const verifiedLabelFor = (row, hostCredentials) => {
  // Tier 2 requires permit AND/OR COI. Show the most specific label:
  //   - "Licensed" when the row requires a license and the host has
  //     a verified food permit on file
  //   - "Insured" when the row requires only a COI and the host has
  //     a verified COI on file
  // Pending / rejected / expired aren't surfaced — the listing should
  // never have been publishable in those states (REVIEW step gating).
  if (!row?.requiresLicense && !row?.requiresCOI) return null;
  const permitVerified =
    hostCredentials?.foodServicePermit?.status === 'verified';
  const coiVerified =
    hostCredentials?.certificateOfInsurance?.status === 'verified';
  if (row.requiresLicense && permitVerified) return 'Licensed';
  if (row.requiresCOI && !row.requiresLicense && coiVerified) return 'Insured';
  return null;
};

const AddOnCard = ({ row, hostCredentials }) => {
  const cat = findAddonCategory(row?.category);
  if (!cat) return null;
  // Renter-facing "from $X": show the ALL-IN floor (base + 15% fee), not
  // the host's raw startingAt. At checkout every quote-sheet row —
  // add-ons included — sums into the offer subtotal that gets the 15%
  // customer markup (server/api-util/lineItems.js
  // calculateNegotiationOfferLineItemsItemized), so showing the bare
  // base here would leak our markup, same as the listing-price surfaces.
  const price = formatStartingAt(priceWithFees(row?.startingAt), row?.pricingUnit);
  const verifiedLabel = verifiedLabelFor(row, hostCredentials);
  return (
    <div className={css.card}>
      <div className={css.cardHeader}>
        <span className={css.cardName}>
          {cat.label}
          {verifiedLabel && (
            <span className={css.cardVerified}>✓ {verifiedLabel}</span>
          )}
        </span>
        {price && (
          <span className={css.cardPrice}>
            from <strong>{price}</strong>
          </span>
        )}
      </div>
      {row?.description && (
        <p className={css.cardDesc}>{row.description}</p>
      )}
    </div>
  );
};

const TierGroup = ({ tier, rows, hostCredentials }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className={css.group}>
      <h3 className={css.groupTitle}>{ADDON_TIER_LABELS[tier]}</h3>
      <div className={css.cardList}>
        {rows.map((row, i) => (
          <AddOnCard
            key={`${row.category}-${i}`}
            row={row}
            hostCredentials={hostCredentials}
          />
        ))}
      </div>
    </div>
  );
};

const SectionAvailableAddOns = props => {
  const { className, listing, authorDisplayName } = props;
  const publicData = listing?.attributes?.publicData || {};
  const addOns = Array.isArray(publicData.addOnCategories)
    ? publicData.addOnCategories
    : [];

  if (addOns.length === 0) return null;

  const author = ensureUser(listing?.author);
  const hostCredentials =
    author?.attributes?.profile?.publicData?.credentials || {};

  // Group by tier, preserving catalog order within each group.
  const rowsByTier = ADDON_TIERS.reduce((acc, tier) => {
    acc[tier] = addOns.filter(row => {
      const cat = findAddonCategory(row?.category);
      return cat && cat.tier === tier;
    });
    return acc;
  }, {});

  return (
    <section className={classNames(css.root, className)}>
      <h2 className={css.title}>Available add-ons</h2>
      <p className={css.subtitle}>
        {authorDisplayName
          ? `Add-on services ${authorDisplayName} offers`
          : 'Add-on services your host offers'}
      </p>
      {ADDON_TIERS.map(tier => (
        <TierGroup
          key={tier}
          tier={tier}
          rows={rowsByTier[tier]}
          hostCredentials={hostCredentials}
        />
      ))}
    </section>
  );
};

// Exported for tests.
export { formatStartingAt, verifiedLabelFor };

export default SectionAvailableAddOns;
